import { ref, onMounted, watch, onUnmounted } from 'vue'
import { useAuth } from './useAuth'
import { doc, getDoc, Timestamp } from 'firebase/firestore'
import { FirebaseProvider } from '../infrastructure/providers/FirebaseProvider'
import { PLAN_IDS } from '../domain/types/Plan';

// Cache global para mantener la información de suscripción
const subscriptionCache = {
  planName: null as string | null,
  daysRemaining: 0,
  userId: null as string | null,
  usageLimit: null as number | null,
  planId: null as string | null
}

// Helper para cargar desde localStorage
const loadFromLocalStorage = (userId: string) => {
  try {
    const cached = localStorage.getItem(`subscription_${userId}`)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch (error) {
    console.error('Error loading subscription cache:', error)
  }
  return null
}

// Helper para guardar en localStorage
const saveToLocalStorage = (userId: string, data: any) => {
  try {
    localStorage.setItem(`subscription_${userId}`, JSON.stringify({
      ...data,
      cachedAt: Date.now()
    }))
  } catch (error) {
    console.error('Error saving subscription cache:', error)
  }
}

export function useSubscription() {
  const { currentUser } = useAuth()
  const planName = ref<string | null>(null)
  const daysRemaining = ref(0)
  const loading = ref(true)
  const usageLimit = ref<number | null>(null)
  const planId = ref<string | null>(null)
  const db = FirebaseProvider.getInstance().getFirestore()

  const calculateDaysRemaining = (endDate: string | Date | Timestamp) => {
    const today = new Date()
    let end: Date

    if (endDate instanceof Timestamp) {
      end = endDate.toDate()
    } else if (endDate instanceof Date) {
      end = endDate
    } else {
      end = new Date(endDate)
    }

    const diffTime = end.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : 0
  }

  const shouldRefreshSubscription = () => {
    // Si no hay usuario actual, no refrescar
    if (!currentUser.value?.uid) return false;
    
    return !subscriptionCache.planName || 
           !subscriptionCache.userId || 
           subscriptionCache.userId !== currentUser.value.uid ||
           subscriptionCache.planId !== planId.value
  }

  // Escuchar el evento personalizado de cambio de plan
  onMounted(() => {
    window.addEventListener('plan-changed', async (event: any) => {
      const newPlanId = event.detail?.planId;
      if (newPlanId && newPlanId !== planId.value) {
        planId.value = newPlanId;
        await fetchSubscriptionInfo();
      }
    });
  });

  onUnmounted(() => {
    window.removeEventListener('plan-changed', () => {});
  });

  const fetchSubscriptionInfo = async () => {
    try {
      // Validar que currentUser existe y tiene uid
      if (!currentUser.value?.uid) {
        planName.value = 'Freemium'
        usageLimit.value = null
        planId.value = PLAN_IDS.freemium
        loading.value = false
        return
      }

      // OPTIMIZACIÓN 1: Cargar desde localStorage inmediatamente
      const cached = loadFromLocalStorage(currentUser.value.uid)
      if (cached) {
        planName.value = cached.planName || 'Freemium'
        usageLimit.value = cached.usageLimit || null
        planId.value = cached.planId || PLAN_IDS.freemium
        daysRemaining.value = cached.daysRemaining || 0
        loading.value = false // Mostrar inmediatamente
      }

      // Si es superadmin, asignar plan especial sin verificar suscripción
      if ((currentUser.value.role as any) === 'superadmin') {
        planName.value = 'Freemium'
        usageLimit.value = null
        planId.value = PLAN_IDS.freemium
        daysRemaining.value = 9999
        loading.value = false
        return
      }

      // OPTIMIZACIÓN 2: Obtener subscription y plan EN PARALELO
      const subscriptionRef = doc(db, 'Subscriptions', currentUser.value.uid)
      const subscriptionDoc = await getDoc(subscriptionRef)

      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data()
        const currentPlanId = subscriptionData.planId || PLAN_IDS.freemium
        planId.value = currentPlanId
        
        // Procesar fechas de trial
        if (subscriptionData.trialEndDate) {
          const calculatedDays = calculateDaysRemaining(subscriptionData.trialEndDate)
          daysRemaining.value = calculatedDays
          
          if (calculatedDays > 0 && currentPlanId === PLAN_IDS.trialPremium) {
            planName.value = 'Trial Premium'
          }
        }

        // Obtener información del plan (ya no bloqueante si hay cache)
        if (subscriptionData.planId) {
          const planRef = doc(db, 'Plans', subscriptionData.planId)
          const planDoc = await getDoc(planRef)
        
          if (planDoc.exists()) {
            const planData = planDoc.data()
            planName.value = planData.name || 'Freemium'
            usageLimit.value = planData.usageLimit

            // Guardar en localStorage y caché
            const dataToCache = {
              planName: planName.value,
              usageLimit: usageLimit.value,
              planId: planId.value,
              daysRemaining: daysRemaining.value
            }
            saveToLocalStorage(currentUser.value.uid, dataToCache)
            
            subscriptionCache.planName = planName.value
            subscriptionCache.usageLimit = usageLimit.value
            subscriptionCache.userId = currentUser.value.uid
            subscriptionCache.planId = planId.value
            subscriptionCache.daysRemaining = daysRemaining.value
          }
        }
      } else {
        planName.value = 'Freemium'
        usageLimit.value = null
        planId.value = PLAN_IDS.freemium
      }
    } catch (error) {
      console.error('Error fetching subscription:', error)
      // Si hay error pero tenemos cache, mantener el cache
      if (!planName.value) {
        planName.value = 'Freemium'
        usageLimit.value = null
        planId.value = PLAN_IDS.freemium
      }
    } finally {
      loading.value = false
    }
  }

  // Observar cambios en el usuario actual y en el planId
  watch([currentUser, planId], async ([newUser, newPlanId], [oldUser, oldPlanId]) => {
    // Solo ejecutar si el usuario es válido o si cambió el planId
    if (newUser && newUser.uid && 
        ((newUser.uid !== oldUser?.uid) || (newPlanId && newPlanId !== oldPlanId))) {
      await fetchSubscriptionInfo()
    }
  }, { immediate: false }) // Cambiar a false para evitar ejecución inmediata con usuario null
  
  // Ejecutar inicial cuando el usuario esté disponible
  watch(currentUser, async (newUser) => {
    if (newUser && newUser.uid && !planName.value) {
      await fetchSubscriptionInfo()
    }
  }, { immediate: true })

  return {
    planName,
    daysRemaining,
    loading,
    usageLimit,
    planId,
    fetchSubscriptionInfo
  }
}
